import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, switchMap, take, tap } from 'rxjs/operators';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { SupportedLanguages, supportedLanguagesMap } from '@ag-common-lib/public-api';

export interface SubversionFileData {
  sourceFileKey: string;
  agreementType: string;
  language: string;
  fileVersion: string;
  masterVersion?: string;
}

export interface DownloadPdfOptions {
  fileName?: string;
  onLoadingChange?: (loading: boolean) => void;
}

@Injectable({
  providedIn: 'root',
})
export class UserAgreementsPdfDownloadService {
  /**
   * Downloads PDFs from subversions and creates a ZIP file
   * @param subversions$ Observable of subversions array with sourceFileKey, agreementType, language, fileVersion
   * @param options Optional configuration for the download
   * @returns Observable that completes when download is finished
   */
  downloadPdfZip(subversions$: Observable<SubversionFileData[]>, options: DownloadPdfOptions = {}): Observable<void> {
    const { fileName, onLoadingChange } = options;
    onLoadingChange?.(true);

    return subversions$.pipe(
      take(1),
      tap(() => onLoadingChange?.(true)),
      switchMap(async subversions => {
        if (!subversions || subversions.length === 0) {
          console.warn('No subversions to download');
          return;
        }

        const urls = subversions
          .filter(data => !!data.sourceFileKey)
          .map(sv => ({
            sourceFileKey: sv.sourceFileKey,
            type: sv.agreementType,
            language: sv.language,
            version: sv.fileVersion,
            masterVersion: sv.masterVersion,
          }));
        const zip = new JSZip();
        for (let i = 0; i < urls.length; i++) {
          try {
            const file = urls[i];
            const response = await fetch(file.sourceFileKey);
            if (!response.ok) {
              console.error(`Failed to fetch ${file.sourceFileKey} with status ${response.status}`);
              continue;
            }
            const blob = await response.blob();
            const watermarkedBlob = await this.addVersionWatermark(blob, file.masterVersion, file.version, file.language);
            const fileLanguage = file.language;
            const languageName =
              supportedLanguagesMap.get(fileLanguage as SupportedLanguages)?.toLowerCase() || fileLanguage;
            const fileName = `${file.type}_${file.version}_${languageName}.pdf`;
            zip.file(fileName, watermarkedBlob);
          } catch (error) {
            console.error('Error fetching file:', error);
          }
        }

        const content = await zip.generateAsync({ type: 'blob' });
        const timeStamp = new Date().toJSON().slice(0, 19);
        const zipFileName = fileName || `user_agreements_${timeStamp}.zip`;
        saveAs(content, zipFileName);
      }),
      finalize(() => {
        onLoadingChange?.(false);
      }),
    );
  }

  private async addVersionWatermark(pdfBlob: Blob, masterVersion?: string, subVersion?: string, language?: string): Promise<Blob> {
    if (!masterVersion && !subVersion) {
      return pdfBlob;
    }

    try {
      const pdfBytes = await pdfBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const watermark = `Master Version: ${masterVersion ?? '-'} | ${language.toUpperCase()} Version: ${subVersion ?? '-'}`;
      const fontSize = 12;
      const color = rgb(0.5, 0.5, 0.5);

      pdfDoc.getPages().forEach(page => {
        const { width, height } = page.getSize();
        const textWidth = font.widthOfTextAtSize(watermark, fontSize);
        const x = Math.max((width - textWidth) / 2, 10);
        const y = height - fontSize * 1.5;
        page.drawText(watermark, {
          x,
          y,
          size: fontSize,
          font,
          color,
          opacity: 0.5,
        });
      });

      const modifiedBytes = await pdfDoc.save();
      return new Blob([modifiedBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Failed to add version watermark to PDF', error);
      return pdfBlob;
    }
  }
}
